import { useEffect, useState } from 'react';
import { listRecentRecipients, type RecentRecipient } from '../api/workflowApi';

/**
 * Pulls the user's most-recent unique signing recipients for autocomplete in
 * the contract velocity HUD. v1 sources from the SigningRequest history only;
 * a future revision can fold in CRM / email contacts.
 */
export interface UseRecentRecipientsResult {
  recipients: RecentRecipient[];
  loading: boolean;
  error: string | null;
}

export function useRecentRecipients(options?: { enabled?: boolean; limit?: number }): UseRecentRecipientsResult {
  const enabled = options?.enabled ?? true;
  const limit = options?.limit ?? 20;

  const [recipients, setRecipients] = useState<RecentRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRecipients([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listRecentRecipients(limit)
      .then((r) => {
        if (!cancelled) setRecipients(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load recipients');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, limit]);

  return { recipients, loading, error };
}
